/* eslint-disable complexity */
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import set from 'lodash/set';
import isUndefined from 'lodash/isUndefined';
import isNil from 'lodash/isNil';

import { ResourceType, STTLTimeNodePresetClassType, STTLTimeNodeType } from '@tencent/slide-sdk-pb-model';
import {
    ANIM_TYPE_TO_TEMPLATE_KEY,
    AnimType,
    PresetId,
    PresetSubtype,
    SHAPE_ID_PATH,
    BehaviorType,
    ENTR_ID_MAP,
    EXIT_ID_MAP,
    EMPH_ID_MAP,
    ArcPresetId,
    CyclePresetId,
    ShapePresetId,
    StraightLinePresetId,
    TurnPresetId,
    PATH_ID_MAP,
    DELAY_RATE_MAP,
    DUR_PATH,
    DELAY_PATH,
    XY_ATTR_NAME_LIST,
    WH_ATTR_NAME_LIST,
    STRETCH_TAV_MAP,
    AttrName,
    MEDIA_VIDEO,
    PAUSE_DURATION,
    MEDIA_AUDIO,
    BehaviorAttrName,
    COLOR_ANIM_LIST,
} from '../constants/timing-constants';
import { JSF32RandomIDGenerator } from '../utils/id-generator';
import unitTimeNodeTemplate from '../template/unit-time-node-template.json';
import tavTemplate from '../template/tav-template.json';
import { animEffectUpdateList, presetSubTypeAnimEffectFilterMap } from './unit-time-node-constant';
import type {
    IEGTimeNodeChoice,
    ICTTLAnimateBehavior,
    ICTTLTimeAnimateValue,
    ICTTLTimeCondition,
    ICTTLPoint,
    ICTTLTimeAnimateValueList,
    ICTTLAnimVariant,
    ICTTLAnimateColorBehavior,
    ICTTLSetBehavior,
    ICTSchemeColor,
    ICTSRgbColor,
    ICTColorDmlA,
} from '@tencent/slide-sdk-pb-model';
import type {
    IPresetSubtypeDict,
    IAnimModifyParam,
    IUpdateTimeNodeByAnimType,
    IColor,
    IColorValue,
    ColorKey,
} from '../constants/timing-constants';

import type { DeepPartial, DeepReadonly } from 'ts-essentials';

export class UnitTimeNode {
    /**
     * 获取动画绑定的 shapeId
     * @param unitTimeNodeData
     * @returns
     */
    public static getShapeId(data: IEGTimeNodeChoice) {
        let shapeId: string | undefined;
        if (data.audio) {
            shapeId = data.audio?.cMediaNode?.tgtEl?.spTgt?.spid;
            if (shapeId) return shapeId;
        }
        data.par?.cTn?.childTnLst?.egTimeNodeChoice?.find((child) => {
            const key = Object.keys(BehaviorType).find((key) => has(child, key));
            const spid = get(child, `${key}.${SHAPE_ID_PATH}`);
            if (spid) {
                shapeId = spid;
                return true;
            }
            return false;
        });
        return shapeId;
    }

    public static getId(data: IEGTimeNodeChoice) {
        return data.par?.cTn?.id ?? data.audio?.cMediaNode?.cTn?.id ?? data.video?.cMediaNode?.cTn?.id;
    }

    /**
     * 获取动画大类(入场、退出、强调)
     * @param unitTimeNode
     * @returns
     */
    public static getPresetClass(data: IEGTimeNodeChoice) {
        return data.par?.cTn?.presetClass;
    }

    private data: IEGTimeNodeChoice;

    constructor(originData: DeepReadonly<IEGTimeNodeChoice>) {
        this.data = originData as IEGTimeNodeChoice;
    }

    public getData() {
        return this.data;
    }

    public setData(data: IEGTimeNodeChoice) {
        this.data = data;
    }

    public get id() {
        return UnitTimeNode.getId(this.data);
    }

    /**
     * 获取动画绑定的 shapeId
     * @returns
     */
    public get shapeId() {
        return UnitTimeNode.getShapeId(this.data);
    }

    /**
     * 获取动画具体类型
     * @param unitTimeNode
     * @returns
     */
    public get animType(): AnimType {
        const { presetId, presetClass } = this.data?.par?.cTn || {};
        if (!presetId || !presetClass) return AnimType.None;
        const animTypeMap = {
            [STTLTimeNodePresetClassType.entr]: ENTR_ID_MAP,
            [STTLTimeNodePresetClassType.exit]: EXIT_ID_MAP,
            [STTLTimeNodePresetClassType.emph]: EMPH_ID_MAP,
            [STTLTimeNodePresetClassType.path]: PATH_ID_MAP,
            [STTLTimeNodePresetClassType.verb]: PATH_ID_MAP,
            [STTLTimeNodePresetClassType.mediacall]: PATH_ID_MAP,
            [STTLTimeNodePresetClassType.PBDEFAULT]: PATH_ID_MAP,
        };
        const idMap = animTypeMap[presetClass];
        if (get(idMap, presetId)) return get(idMap, presetId) as AnimType;
        if (presetClass === STTLTimeNodePresetClassType.path) return AnimType.Custom;
        return AnimType.None;
    }

    /**
     * 获取动画大类(入场、退出、强调)
     * @param unitTimeNode
     * @returns
     */
    public get presetClass() {
        return UnitTimeNode.getPresetClass(this.data);
    }

    /**
     * 获取动画种类ID
     * @param unitTimeNode
     * @returns
     */
    public get presetId() {
        return this.data.par?.cTn?.presetId;
    }

    /**
     * 获取动画方向
     * @param unitTimeNode
     * @returns
     */
    public get presetSubtype() {
        return this.data.par?.cTn?.presetSubtype;
    }

    /**
     * 从动画数据中获取透明度
     * @param unitTimeNode
     * @returns
     */
    public get opacity(): string | undefined {
        if (!this.data?.par?.cTn) return;
        const { presetId, presetClass } = this.data.par.cTn;
        this.data.par.cTn.childTnLst?.egTimeNodeChoice?.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (
                presetId === PresetId.EmphTransparency &&
                presetClass === STTLTimeNodePresetClassType.emph &&
                behaviorKey === BehaviorType.set
            ) {
                return get(tnBase, `${behaviorKey}.to.strVal.val`);
            }
        });
    }

    public get delay(): string {
        return get(this.data, 'par.cTn.stCondLst.cond[0].delay', '0');
    }

    public get nodeType() {
        return this.data.par?.cTn?.nodeType || STTLTimeNodeType.PBDEFAULT;
    }

    public get by() {
        const childTnList = this.data.par?.cTn?.childTnLst?.egTimeNodeChoice || [];
        let by: number | string | ICTTLPoint | undefined;
        childTnList.find((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);

            if (
                behaviorKey === BehaviorType.animRot ||
                behaviorKey === BehaviorType.animScale ||
                behaviorKey === BehaviorType.anim
            ) {
                by = tnBase[behaviorKey as 'animRot' | 'animScale' | 'anim']?.by;
                return !!by;
            }
            return false;
        });
        return by;
    }

    /**
     * 从动画中获取持续时间
     * @param unitTimeNode
     * @returns
     */
    public get dur() {
        const childTnLst = this.data.par?.cTn?.childTnLst?.egTimeNodeChoice || [];
        let maxDur = 1; // 动画持续时间最小为1ms
        childTnLst.forEach((tnBase) => {
            const qualifiedAnimTypeArr = [
                BehaviorType.animEffect,
                BehaviorType.animScale,
                BehaviorType.animRot,
                BehaviorType.anim,
                BehaviorType.animMotion,
                BehaviorType.cmd,
                BehaviorType.animClr,
            ];
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (qualifiedAnimTypeArr.includes(behaviorKey)) {
                let dur = get(tnBase, `${behaviorKey}.${DUR_PATH}`, 0);
                const delay = get(tnBase, `${behaviorKey}.${DELAY_PATH}`, 0);

                if (dur === 'indefinite') {
                    dur = 500;
                }
                dur = tnBase[behaviorKey]?.cBhvr?.cTn?.autoRev ? Number(dur) * 2 : Number(dur);
                const totalDur = +dur + +delay;
                if (maxDur < totalDur) {
                    maxDur = totalDur;
                }
            }
        });
        if (this.presetId === PresetId.BrushColor && this.presetClass === STTLTimeNodePresetClassType.emph) {
            // 强调动画-画笔颜色特殊处理，其子动画中只有setBehavior
            maxDur = Number(childTnLst[0]?.set?.cBhvr?.cTn?.dur ?? 1);
        }
        return Math.round(maxDur);
    }

    public get behaviorList() {
        return this.data.par?.cTn?.childTnLst?.egTimeNodeChoice;
    }

    public get color(): IColor | undefined {
        const choiceList = this.behaviorList;
        if (!choiceList) return;
        let setBehaviorClr: ICTColorDmlA | undefined;
        for (const choice of choiceList) {
            const [behaviorName] = Object.keys(choice);
            if (behaviorName === BehaviorType.animClr) {
                const behaviorObj = choice[behaviorName];
                const toObj = behaviorObj?.to;
                if (!toObj) return;
                return this.getColorFromToObject(toObj);
            }
            if (!setBehaviorClr && behaviorName === BehaviorType.set && choice.set?.to?.clrVal) {
                setBehaviorClr = choice.set.to.clrVal;
            }
        }
        if (setBehaviorClr) {
            return this.getColorFromToObject(setBehaviorClr);
        }
    }

    /**
     * 是否是缩放动画
     */
    public get isScale() {
        return [AnimType.EnterScale, AnimType.ExitScale].includes(this.animType);
    }

    /**
     * 设置动画的绑定的 shapeId
     * @param unitTimeNode
     * @param shapeId
     */
    public setShapeId(shapeId: string) {
        this.data.par?.cTn?.childTnLst?.egTimeNodeChoice?.forEach((child) => {
            const key = Object.keys(BehaviorType).find((key) => has(child, key));
            if (!key) return;
            set(child, `${key}.${SHAPE_ID_PATH}`, shapeId);
        });
    }

    /**
     * 设置 unitTimeNode 的持续时间
     * @param dur
     * @returns
     */
    public setDur(dur: number) {
        if (COLOR_ANIM_LIST.includes(this.animType)) {
            this.setColorAnimDur(dur);
            return;
        }
        const childTnList = this.data.par?.cTn?.childTnLst?.egTimeNodeChoice || [];
        const handlerMap = {
            [AnimType.EntrRiseUp]: () => this.riseAndSinkDurHandler(AnimType.EntrRiseUp, childTnList, dur),
            [AnimType.ExitSinkDown]: () => this.riseAndSinkDurHandler(AnimType.ExitSinkDown, childTnList, dur),
            [AnimType.EntrBoomerang]: () => this.entryBoomerangDurHandler(childTnList, dur),
            [AnimType.ExitBoomerang]: () => this.exitBoomerangDurHandler(childTnList, dur),
            [AnimType.EntrCenterRevolve]: () => this.centerRevolveHandler(AnimType.EntrCenterRevolve, childTnList, dur),
            [AnimType.ExitCenterRevolve]: () => this.centerRevolveHandler(AnimType.ExitCenterRevolve, childTnList, dur),
            [AnimType.EntrFloat]: () => this.bounceAndFloatHandler(AnimType.EntrFloat, childTnList, dur),
            [AnimType.ExitFloat]: () => this.bounceAndFloatHandler(AnimType.ExitFloat, childTnList, dur),
            [AnimType.EntrBounce]: () => this.bounceAndFloatHandler(AnimType.EntrBounce, childTnList, dur),
            [AnimType.ExitBounce]: () => this.bounceAndFloatHandler(AnimType.ExitBounce, childTnList, dur),
        };
        const handler = get(handlerMap, this.animType);
        if (handler) {
            handler();
            return;
        }
        childTnList.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (this.isAnimDur(behaviorKey)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(dur));
            }
            if (this.isVisibilityDur(this.animType, behaviorKey, dur)) {
                set(tnBase, `${behaviorKey}.${DELAY_PATH}`, String(dur - 1));
            }
        });
    }

    /**
     * 设置动画方向
     * @param this.data
     * @param presetSubtype
     * @returns
     */
    public setPresetSubType(presetSubtype: PresetSubtype) {
        if (!this.data.par?.cTn?.childTnLst?.egTimeNodeChoice) return;
        this.data.par.cTn.presetSubtype = presetSubtype;

        const childTnLst = this.data.par.cTn.childTnLst.egTimeNodeChoice;
        // 部分动画需要同时更新 animEffect 标签
        if (animEffectUpdateList.includes(this.animType)) {
            const animEffectFilterList = presetSubTypeAnimEffectFilterMap[this.animType];
            if (animEffectFilterList) {
                const tnBase = childTnLst.find((tnBase) => !!tnBase.animEffect);
                const animEffectFilter = animEffectFilterList[presetSubtype];
                if (tnBase?.animEffect && animEffectFilter) {
                    tnBase.animEffect.filter = animEffectFilter;
                }
            }
        }
        // CUT_IN 和 CUT_OUT 需要同时更新 animEffect⬆️ 和 tavLst⬇️ 中值
        if (this.presetId === PresetId.Cut || this.presetId === PresetId.Fly) {
            this.setTavLstWithSubType(presetSubtype);
            return;
        }
        const childTnLstRoot = this.data.par.cTn.childTnLst;
        if (this.presetId === PresetId.EntrStretch && this.presetClass === STTLTimeNodePresetClassType.entr) {
            // presetSubtype 跨(10)只有2个anim标签
            // presetSubtype 从顶端(1),从底端(4), 从左侧(8),从右侧(2),需要4个anim标签，
            if (presetSubtype === PresetSubtype.Across) {
                if (childTnLst.length <= 3) return;
                childTnLstRoot.egTimeNodeChoice = this.handleAcrossAnimTag(childTnLst);
                return;
            }
            childTnLstRoot.egTimeNodeChoice = this.handleNotAcrossAnimTag(childTnLst, presetSubtype);
            return;
        }

        if (
            this.animType !== AnimType.ExitCollapse &&
            this.animType !== AnimType.EntrBasicSwivel &&
            this.animType !== AnimType.ExitBasicSwivel &&
            this.animType !== AnimType.EntrBasicZoom &&
            this.animType !== AnimType.ExitBasicZoom &&
            this.animType !== AnimType.EnterScale &&
            this.animType !== AnimType.ExitScale
        )
            return;
        ({
            [AnimType.ExitCollapse]: () => this.handleStretch(childTnLst, presetSubtype),
            [AnimType.EntrBasicSwivel]: () => this.handleEntrSwivel(childTnLst, presetSubtype),
            [AnimType.ExitBasicSwivel]: () => this.handleExitSwivel(childTnLst, presetSubtype),
            [AnimType.EntrBasicZoom]: () => this.handleEntrZoom(childTnLst, presetSubtype),
            [AnimType.ExitBasicZoom]: () => this.handleExitZoom(childTnLst, presetSubtype),
            [AnimType.EnterScale]: () => this.handleEntrScale(childTnLst, presetSubtype),
            [AnimType.ExitScale]: () => this.handleExitScale(childTnLst, presetSubtype),
        })[this.animType]();
    }

    /**
     * 设置动画的延迟时间
     * @param unitTimeNode
     * @param delay
     */
    public setDelay(delay: string) {
        this.data.par?.cTn?.stCondLst?.cond?.forEach((i) => (i.delay = delay));
    }

    /**
     * 设置动画透明度
     * @param this.data
     * @param opacity
     * @returns
     */
    public setOpacity(opacity: string) {
        if (!this.data?.par?.cTn) return;
        const { presetId, presetClass } = this.data.par.cTn;
        this.data.par.cTn.childTnLst?.egTimeNodeChoice?.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (
                presetId === PresetId.EmphTransparency &&
                presetClass === STTLTimeNodePresetClassType.emph &&
                behaviorKey === BehaviorType.set
            ) {
                set(tnBase, `${behaviorKey}.to.strVal.val`, opacity);
            }
        });
    }

    /**
     * 设置动画颜色数据
     * @param color IColor 主题色色值 | 非主题rgb颜色值
     */
    public setColor(color: IColor): void {
        const choiceList = this.data?.par?.cTn?.childTnLst?.egTimeNodeChoice;
        if (!choiceList) return;
        const handlerMap: Record<
            string,
            (behavior: ICTTLAnimateColorBehavior | ICTTLSetBehavior, color: IColor) => void
        > = {
            animClr: (behavior, color) => this.handleAnimClr(behavior as ICTTLAnimateColorBehavior, color),
            set: (behavior, color) => this.handleSetBehaviorClr(behavior as ICTTLSetBehavior, color),
        };
        for (const choice of choiceList) {
            Object.entries(choice).forEach(([key, value]) => {
                handlerMap[key]?.(value, color);
            });
        }
    }

    public setBy(by: number | ICTTLPoint | string) {
        this.data.par?.cTn?.childTnLst?.egTimeNodeChoice?.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (
                (typeof by === 'number' && behaviorKey === BehaviorType.animRot) ||
                (typeof by === 'string' && behaviorKey === BehaviorType.anim) ||
                (typeof by === 'object' && behaviorKey === BehaviorType.animScale)
            ) {
                set(tnBase, `${behaviorKey}.by`, by);
            }
        });
    }

    public setNodeType(nodeType: STTLTimeNodeType) {
        set(this.data, 'par.cTn.nodeType', nodeType);
    }

    public setId(id: string) {
        if (this.data?.par?.cTn) {
            Object.defineProperty(this.data.par.cTn, 'id', { value: id });
        } else if (this.data?.audio?.cMediaNode?.cTn) {
            Object.defineProperty(this.data.audio?.cMediaNode?.cTn, 'id', { value: id });
        } else if (this.data?.video?.cMediaNode?.cTn) {
            Object.defineProperty(this.data.video?.cMediaNode?.cTn, 'id', { value: id });
        }
    }

    /**
     * 设置音频循环播放
     * @param repeatCount
     * @returns
     */
    public setMediaRepeatCount(repeatCount?: string) {
        const mediaType = this.data?.audio ? 'audio' : 'video';
        const basePath = `${mediaType}.cMediaNode`;
        if (!get(this.data, basePath)) return;
        if (!isNil(repeatCount)) {
            set(this.data, `${basePath}.cTn.repeatCount`, repeatCount);
        } else {
            delete this.data[mediaType]?.cMediaNode?.cTn?.repeatCount;
        }
    }

    /**
     * 设置音频跨页播放
     * @param numSld
     * @returns
     */
    public setMediaNumSld(numSld?: number) {
        const mediaType = this.data?.audio ? 'audio' : 'video';
        const basePath = `${mediaType}.cMediaNode`;
        if (!get(this.data, basePath)) return;
        if (!isNil(numSld)) {
            set(this.data, `${basePath}.numSld`, numSld);
        } else {
            delete this.data[mediaType]?.cMediaNode?.numSld;
        }
    }

    /**
     * 设置媒体音量
     * @param vol
     * @returns
     */
    public setMediaVolume(vol?: number) {
        const mediaType = this.data?.audio ? 'audio' : 'video';
        const basePath = `${mediaType}.cMediaNode`;
        if (!get(this.data, basePath)) return;
        if (!isNil(vol)) {
            set(this.data, `${basePath}.vol`, vol);
        } else {
            delete this.data[mediaType]?.cMediaNode?.vol;
        }
    }

    /**
     * 根据动画类型创建动画
     * @param animType
     * @param shapeId
     * @param duration
     * @returns
     */
    public updateTimeNodeByAnimType(updateTimeNodeByAnimType: IUpdateTimeNodeByAnimType) {
        const { animType, shapeId, duration, mediaType } = updateTimeNodeByAnimType;
        const key = get(ANIM_TYPE_TO_TEMPLATE_KEY, animType);
        if (!key || !has(unitTimeNodeTemplate, key)) {
            return {};
        }
        this.data = cloneDeep(get(unitTimeNodeTemplate, key, {}) as IEGTimeNodeChoice);
        this.setShapeId(shapeId);
        if ([AnimType.Disappear].includes(animType)) {
            set(this.data, 'par.cTn.presetClass', STTLTimeNodePresetClassType.exit);
        }
        if ([AnimType.FlyOut, AnimType.FloatUpOut, AnimType.FloatDownOut, AnimType.CutOut].includes(animType)) {
            set(this.data, 'par.cTn.presetClass', STTLTimeNodePresetClassType.exit);
            this.setTavLstWithSubType(PresetSubtype.Bottom);
        }
        if ([AnimType.Play, AnimType.Pause, AnimType.Stop].includes(animType)) {
            const baseKey = 'par.cTn.childTnLst.egTimeNodeChoice[0].cmd.cBhvr';
            const mediaKey = mediaType === ResourceType.Video ? 'video.cMediaNode' : 'audio.cMediaNode';
            const mediaData = mediaType === ResourceType.Video ? cloneDeep(MEDIA_VIDEO) : cloneDeep(MEDIA_AUDIO);
            const mediaDuration =
                animType === AnimType.Pause && mediaType === ResourceType.Video ? PAUSE_DURATION : duration;
            set(this.data, `${baseKey}.cTn.id`, new JSF32RandomIDGenerator().next());
            set(this.data, `${baseKey}.tgtEl.spTgt.spid`, shapeId);
            set(this.data, `${baseKey}.cTn.dur`, mediaDuration);
            set(this.data, mediaType === ResourceType.Video ? 'video' : 'audio', mediaData);
            set(this.data, `${mediaKey}.cTn.id`, new JSF32RandomIDGenerator().next());
            set(this.data, `${mediaKey}.tgtEl.spTgt.spid`, shapeId);
        }
    }

    /**
     * 获取 tnBase 类型，含义和原有 tn_xx 一致
     * @param tnBase
     * @returns
     */

    public getBehaviorKeyFromTnBase(tnBase: DeepReadonly<IEGTimeNodeChoice>) {
        return Object.keys(BehaviorType).find((key) => has(tnBase, key)) as BehaviorType;
    }

    public setAttrNameInTnBase(tnBase: DeepReadonly<IEGTimeNodeChoice>, attrName: AttrName) {
        const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
        set(tnBase, `${behaviorKey}.cBhvr.attrNameLst.attrName.0`, attrName);
    }

    public getAttrNameFromTnBase(tnBase: DeepReadonly<IEGTimeNodeChoice>) {
        const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
        return tnBase[behaviorKey]?.cBhvr?.attrNameLst?.attrName?.[0] as AttrName | undefined;
    }

    public getTavLstFromTnBase(tnBase: DeepReadonly<IEGTimeNodeChoice>) {
        const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
        if (behaviorKey !== BehaviorType.anim) return;
        const anim = tnBase[behaviorKey] as DeepReadonly<ICTTLAnimateBehavior> | undefined;
        return anim?.tavLst?.tav;
    }

    /**
     * tnBase 是否是 animXXX 类型
     * @param behaviorKey
     * @returns
     */
    public isAnimDur(behaviorKey: BehaviorType) {
        const tnTypeList = [
            BehaviorType.anim,
            BehaviorType.animEffect,
            BehaviorType.animMotion,
            BehaviorType.animRot,
            BehaviorType.animScale,
        ];
        return tnTypeList.includes(behaviorKey);
    }

    public isVisibilityDur(animType: AnimType, behaviorKey: string, dur: number) {
        if (typeof dur !== 'number') return false;
        if (behaviorKey !== BehaviorType.set) return false;
        const animTypeList = [
            AnimType.FadeOut, // 退场-淡出
            AnimType.ExitScale, // 退场-缩放
            AnimType.ExitCollapse, // 退场-层叠
            AnimType.ExitSpinner, // 退场-回旋
            AnimType.ExitSpread, // 退场-收缩
            AnimType.ExitShrinkTurn, // 退场-收缩并旋转
        ];
        return animTypeList.includes(animType);
    }

    /**
     *  获取不同动画类型的子动画属性与子动画时间的map
     * @param animType 动画类型
     * @param dur 动画总持续时间
     * @param delay 子动画延迟时间
     * @returns 子动画属性与子动画时间的map（包括持续时间和延迟时间）
     */
    public getTypeTimeMap(
        animType: AnimType.EntrCenterRevolve | AnimType.ExitCenterRevolve,
        dur: number,
        delay: string | undefined,
    ) {
        const fadeInDurRate = 0.1;
        const xyDurRate = 0.4;
        if (animType === AnimType.ExitCenterRevolve) {
            // 退场-中心旋转动画
            return {
                [BehaviorType.set]: { durTime: 1, delayTime: dur - 1 },
                [BehaviorType.animEffect]: {
                    durTime: fadeInDurRate * dur,
                    delayTime: (1 - fadeInDurRate) * dur,
                },
                [BehaviorType.anim]: {
                    // 退场-中心旋转动画不会出现delay为undefined的情况，delay为'0'，则设置为0
                    durTime: Number(delay) ? xyDurRate * dur : (1 - xyDurRate) * dur,
                    delayTime: Number(delay) ? (1 - xyDurRate) * dur : 0,
                },
            };
        }
        // 默认返回入场-中心旋转动画的map值
        // 入场-中心旋转动画：delay为undefined，则不需要设置；delay为'0'，设置0
        return {
            [BehaviorType.set]: { durTime: 1 },
            [BehaviorType.animEffect]: { durTime: fadeInDurRate * dur },
            [BehaviorType.anim]: {
                durTime: isUndefined(delay) ? xyDurRate * dur : (1 - xyDurRate) * dur,
                delayTime: isUndefined(delay) ? delay : xyDurRate * dur,
            },
        };
    }

    public updateData(optionalParams: DeepPartial<IAnimModifyParam>) {
        const {
            animType,
            nodeType,
            presetSubtype,
            delay,
            dur,
            by,
            opacity,
            shapeId,
            id,
            duration,
            mediaType,
            audio,
            color,
        } = optionalParams;
        if (!animType || !shapeId) return;
        this.updateTimeNodeByAnimType({ animType, shapeId, duration, mediaType });
        this.updateDataExpectAnimType({ nodeType, presetSubtype, delay, dur, by, opacity, shapeId, id, audio, color });
    }

    /**
     * 修改路径动画等不支持的动画时，不通过 updateData 重新生成，直接更新原始 data
     * @param optionalParams
     * @returns
     */
    public updateDataExpectAnimType(optionalParams: DeepPartial<Omit<IAnimModifyParam, 'animType'>>) {
        const { nodeType, presetSubtype, delay, dur, by, opacity, id, audio, video, color } = optionalParams;
        const media = audio || video;
        if (media) {
            if (has(media, 'repeatCount')) {
                this.setMediaRepeatCount(media?.repeatCount);
            }
            if (has(media, 'numSld')) {
                this.setMediaNumSld(media?.numSld);
            }
            if (has(media, 'volume')) {
                this.setMediaVolume(media?.volume);
            }
        }
        if (!this.data?.par?.cTn) return;
        if (!isUndefined(nodeType)) {
            this.data.par.cTn.nodeType = nodeType;
        }
        if (!isUndefined(presetSubtype)) {
            this.setPresetSubType(presetSubtype);
        }
        if (!isUndefined(delay)) {
            this.setDelay(delay);
        }
        if (!isUndefined(dur)) {
            this.setDur(+dur);
        }
        if (!isUndefined(by)) {
            this.setBy(by);
        }
        if (!isUndefined(opacity)) {
            this.setOpacity(opacity);
        }
        if (!isUndefined(id)) {
            this.setId(id);
        }
        if (!isUndefined(color)) {
            this.setColor(color as IColor);
        }
    }

    private setColorAnimDur(dur: number): void {
        this.data.par?.cTn?.childTnLst?.egTimeNodeChoice?.forEach((choice) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(choice);
            const autoRev = choice[behaviorKey]?.cBhvr?.cTn?.autoRev;
            const duration = autoRev ? dur / 2 : dur;
            set(choice, `${behaviorKey}.${DUR_PATH}`, String(duration));
        });
    }

    private handleAnimClr(behavior: ICTTLAnimateColorBehavior, color: IColor) {
        const attrName = behavior.cBhvr?.attrNameLst?.attrName?.[0];
        if (!attrName) return;
        const attrList = [BehaviorAttrName.FILL_COLOR, BehaviorAttrName.FONT_COLOR, BehaviorAttrName.LINE_COLOR];
        if (!attrList.includes(attrName as BehaviorAttrName)) return;
        if (!behavior?.to) return;
        this.updateColor(behavior.to, color);
    }

    private handleSetBehaviorClr(behavior: ICTTLSetBehavior, color: IColor) {
        if (!behavior.to?.clrVal) return;
        this.updateColor(behavior.to.clrVal, color);
    }

    private updateColor(target: ICTColorDmlA, color: IColor) {
        if (!target) return;

        const [[colorKey, colorValue]] = Object.entries(color) as [ColorKey, IColorValue][];
        Object.keys(target).forEach((key) => {
            (target[colorKey] as ICTSchemeColor | ICTSRgbColor) = { val: colorValue };
            if (key !== colorKey) {
                delete target[key as ColorKey];
            }
        });
    }

    private getColorFromToObject(toObj: ICTColorDmlA): IColor | undefined {
        if (!toObj) return;
        const [key] = Object.keys(toObj);
        if (!key) return;
        const result = { [key]: (toObj[key as keyof ICTColorDmlA] as ICTSchemeColor | ICTSRgbColor)?.val };
        return result as IColor;
    }

    /**
     * 修改target的dur和delay时间
     * @param param0
     */
    private setTime({
        tnBase,
        durPath,
        durTime,
        delayPath,
        delayTime,
    }: {
        tnBase: IEGTimeNodeChoice;
        durPath: string;
        durTime: number | undefined;
        delayPath: string;
        delayTime: number | undefined;
    }) {
        const timeList: { path: string; time: string }[] = [];
        if (!isUndefined(durPath) && !isUndefined(durTime)) timeList.push({ path: durPath, time: String(durTime) });
        if (!isUndefined(delayPath) && !isUndefined(delayTime))
            timeList.push({ path: delayPath, time: String(delayTime) });
        timeList.forEach(({ path, time }) => set(tnBase, path, time));
    }

    /**
     * 中心旋转动画 更改时间时需同时更改子动画的delay时间
     * 中心旋转动画是由6个child子动画组成，子动画需要设置delay时间
     * @param animType 动画类型
     * @param childTnList 动画child
     * @param dur 动画总持续时间
     */
    private centerRevolveHandler(
        animType: AnimType.EntrCenterRevolve | AnimType.ExitCenterRevolve,
        childTnList: IEGTimeNodeChoice[],
        dur: number,
    ) {
        childTnList.forEach((tnBase) => {
            if (!tnBase) return;
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (!behaviorKey) return;
            const delay = get(tnBase, `${behaviorKey}.${DELAY_PATH}`);
            const typeTimeMap = this.getTypeTimeMap(animType, dur, delay);
            this.setTime({
                tnBase,
                durPath: `${behaviorKey}.${DUR_PATH}`,
                durTime: get(typeTimeMap, `${behaviorKey}.durTime`),
                delayPath: `${behaviorKey}.${DELAY_PATH}`,
                delayTime: get(typeTimeMap, `${behaviorKey}.delayTime`),
            });
        });
    }

    /**
     * 弹跳/浮动动画 更改时间时需同时更改子动画的delay时间
     * @param animType 动画类型
     * @param childTnList 动画child
     * @param dur 动画总持续时间
     */
    private bounceAndFloatHandler(
        animType: AnimType.EntrBounce | AnimType.ExitBounce | AnimType.EntrFloat | AnimType.ExitFloat,
        childTnList: IEGTimeNodeChoice[],
        dur: number,
    ) {
        childTnList.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (!behaviorKey) return;
            const delay = get(tnBase, `${behaviorKey}.${DELAY_PATH}`);
            const duration = get(tnBase, `${behaviorKey}.${DUR_PATH}`);

            if (!delay || !duration || behaviorKey === BehaviorType.set) return;

            const delayRate = +delay / (+delay + +duration);
            const durRate = +duration / (+delay + +duration);
            set(tnBase, `${behaviorKey}.${DUR_PATH}`, `${Math.round(durRate * dur)}`);
            set(tnBase, `${behaviorKey}.${DELAY_PATH}`, `${Math.round(delayRate * dur)}`);
        });
    }

    /**
     * 入场-飞旋动画的持续时间处理
     * @param childTnList
     * @param dur
     */
    private entryBoomerangDurHandler(childTnList: IEGTimeNodeChoice[], dur: number) {
        const delayRate = 0.5;
        childTnList.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (!behaviorKey) return;
            const attrName = this.getAttrNameFromTnBase(tnBase);
            const delay = get(tnBase, `${behaviorKey}.${DELAY_PATH}`);
            if (behaviorKey === BehaviorType.set) return;
            if (this.checkOnlySetDurWithoutRate(behaviorKey, attrName)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(dur));
                return;
            }
            if (this.checkBoomerangDur(behaviorKey, attrName, delay)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(delayRate * dur));
                return;
            }
            set(tnBase, `${behaviorKey}.${DUR_PATH}`, String((1 - delayRate) * dur));
            set(tnBase, `${behaviorKey}.${DELAY_PATH}`, String(delayRate * dur));
        });
    }

    // 仅设置dur，且需按比例设置
    private checkOnlySetDurByRate(
        behaviorKey: BehaviorType,
        attrName: AttrName | undefined,
        delay: string | undefined,
    ) {
        if (behaviorKey !== BehaviorType.anim) return false;
        if (attrName && [AttrName.pptW, AttrName.pptY].includes(attrName) && !Number(delay)) return true;
        return false;
    }

    // 退场动画-飞旋
    private exitBoomerangDurHandler(childTnList: IEGTimeNodeChoice[], dur: number) {
        const delayRate = 0.5;
        childTnList.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (!behaviorKey) return;
            const attrName = this.getAttrNameFromTnBase(tnBase);
            const delay = get(tnBase, `${behaviorKey}.${DELAY_PATH}`);
            if (behaviorKey === BehaviorType.set) {
                set(tnBase, `${behaviorKey}.${DELAY_PATH}`, String(Number(dur) - 1));
                return;
            }
            if (this.checkOnlySetDurWithoutRate(behaviorKey, attrName)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(dur));
                return;
            }
            if (this.checkOnlySetDurByRate(behaviorKey, attrName, delay)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(delayRate * dur));
                return;
            }
            set(tnBase, `${behaviorKey}.${DUR_PATH}`, String((1 - delayRate) * dur));
            set(tnBase, `${behaviorKey}.${DELAY_PATH}`, String(delayRate * dur));
        });
    }

    /**
     * 入场/退出-升起/下沉动画的持续时间处理
     * @param animType
     * @param childTnList
     * @param dur
     */
    private riseAndSinkDurHandler(
        animType: AnimType.EntrRiseUp | AnimType.ExitSinkDown,
        childTnList: IEGTimeNodeChoice[],
        dur: number,
    ) {
        const delayRate = DELAY_RATE_MAP[animType];
        childTnList.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (!behaviorKey) return;
            const attrName = this.getAttrNameFromTnBase(tnBase);
            const condLst = tnBase[behaviorKey]?.cBhvr?.cTn?.stCondLst?.cond;
            if (this.checkDirectDur(behaviorKey, attrName)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(dur));
            }
            if (this.checkYNoStCondLst(behaviorKey, attrName, condLst)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(delayRate * dur));
            }
            if (this.checkYWithStCondLst(behaviorKey, attrName, condLst)) {
                set(tnBase, `${behaviorKey}.${DUR_PATH}`, String(Math.round((1 - delayRate) * dur)));
                set(tnBase, `${behaviorKey}.${DELAY_PATH}`, String(Math.round(delayRate * dur)));
            }
            if (behaviorKey === BehaviorType.set) {
                const delayMap = {
                    [AnimType.EntrRiseUp]: '0',
                    [AnimType.ExitSinkDown]: String(dur - 1),
                };
                set(tnBase, `${behaviorKey}.${DELAY_PATH}`, delayMap[animType]);
                return;
            }
        });
    }

    // 仅设置dur，且全量设置，无需设置delay
    private checkOnlySetDurWithoutRate(behaviorKey: BehaviorType, attrName?: AttrName) {
        if (behaviorKey === BehaviorType.animEffect) return true;
        if (behaviorKey !== BehaviorType.anim) return false;
        return attrName === AttrName.pptH;
    }

    private checkBoomerangDur(behaviorKey: BehaviorType, attrName?: AttrName, delay?: string) {
        if (behaviorKey !== BehaviorType.anim) return false;
        if (!attrName) return false;
        if ([AttrName.rotation, AttrName.pptX].includes(attrName)) return true;
        if ([AttrName.pptW, AttrName.pptY].includes(attrName) && !Number(delay)) return true;
        return false;
    }

    private checkDirectDur(behaviorKey: BehaviorType, attrName?: AttrName) {
        if (behaviorKey === BehaviorType.animEffect) return true;
        if (behaviorKey !== BehaviorType.anim) return false;
        return attrName === AttrName.pptX;
    }

    private checkYNoStCondLst(
        behaviorKey: BehaviorType,
        attrName: AttrName | undefined,
        condLst: ICTTLTimeCondition[] | undefined,
    ) {
        if (behaviorKey !== BehaviorType.anim) return false;
        if (attrName !== AttrName.pptY) return false;
        return !condLst;
    }

    private checkYWithStCondLst(
        behaviorKey: BehaviorType,
        attrName: AttrName | undefined,
        condLst: ICTTLTimeCondition[] | undefined,
    ) {
        if (behaviorKey !== BehaviorType.anim) return false;
        if (attrName !== AttrName.pptY) return false;
        return !!condLst;
    }

    private setAnimNodeTavLstWithStrings(anim: ICTTLAnimateBehavior, first: string, second: string) {
        anim.tavLst?.tav?.forEach((tav: ICTTLTimeAnimateValue, index: number) => {
            if (index === 0) {
                set(tav, 'val.strVal.val', first);
            } else {
                set(tav, 'val.strVal.val', second);
            }
        });
    }

    /**
     * 处理X轴动画
     * @param anim
     * @param presetClass
     * @param presetId
     * @param presetSubtype
     * @param targetPresetSubType
     * @returns boolean 是否已对 x 做变更
     */
    private processX(
        anim: ICTTLAnimateBehavior,
        presetClass: STTLTimeNodePresetClassType,
        presetId: number | undefined,
        presetSubtype: number | undefined,
        targetPresetSubType: PresetSubtype,
    ): boolean {
        const { tavFlagXDict } = tavTemplate;
        if ([STTLTimeNodePresetClassType.entr, STTLTimeNodePresetClassType.exit].includes(presetClass)) {
            if (presetId === PresetId.Fly || presetId === PresetId.Cut) {
                const presetSubtypeDict = get(
                    tavFlagXDict,
                    `${presetClass}.${presetId}.${presetSubtype}`,
                ) as unknown as IPresetSubtypeDict;
                if (presetSubtypeDict) {
                    // 需要更新 cBhvr.attrName，否则导出时 Office 会使用默认 cBhvr.attrName 的值替换 val.strVal.val 中变量值
                    const includeAttr = anim.cBhvr?.attrNameLst?.attrName?.find((attr) => attr === AttrName.pptX);
                    if (!includeAttr) {
                        // 替换 attrName
                        anim.cBhvr?.attrNameLst?.attrName?.splice(0, 1, AttrName.pptX);
                    }
                    this.setAnimNodeTavLstWithStrings(anim, presetSubtypeDict.first, presetSubtypeDict.second);
                    return true;
                }
            }
            // 从 ooxml 上并无看体现，历史逻辑先注释
            void targetPresetSubType;
            // set(anim, 'cBhvr.by', targetPresetSubType.toString());
        }
        return false;
    }

    /**
     * 处理Y轴动画
     * @param anim
     * @param presetClass
     * @param presetId
     * @param presetSubtype
     * @param targetPresetSubType
     * @returns boolean 是否已对 y 做变更
     */
    private processY(
        anim: ICTTLAnimateBehavior,
        presetClass: STTLTimeNodePresetClassType,
        presetId: number | undefined,
        presetSubtype: number | undefined,
        targetPresetSubType: PresetSubtype,
    ): boolean {
        const { tavFlagYDict } = tavTemplate;
        if ([STTLTimeNodePresetClassType.entr, STTLTimeNodePresetClassType.exit].includes(presetClass)) {
            if (presetId === PresetId.Fly || presetId === PresetId.Cut) {
                const presetSubtypeDict: IPresetSubtypeDict | undefined = get(
                    tavFlagYDict,
                    `${presetClass}.${presetId}.${presetSubtype}`,
                ) as unknown as IPresetSubtypeDict;
                if (presetSubtypeDict) {
                    // 需要更新 cBhvr.attrName，否则导出时 Office 会使用默认 cBhvr.attrName 的值替换 val.strVal.val 中变量值
                    const includeAttr = anim.cBhvr?.attrNameLst?.attrName?.find((attr) => attr === AttrName.pptY);
                    if (!includeAttr) {
                        // 替换 attrName
                        anim.cBhvr?.attrNameLst?.attrName?.splice(0, 1, AttrName.pptY);
                    }
                    this.setAnimNodeTavLstWithStrings(anim, presetSubtypeDict.first, presetSubtypeDict.second);
                    return true;
                }
            }
            // 从 ooxml 上并无看体现，历史逻辑先注释
            void targetPresetSubType;
            // set(anim, 'cBhvr.by', targetPresetSubType.toString());
        }
        return false;
    }

    /**
     * 处理 ACROSS 方向
     * @param childTnLst
     * @returns
     */
    private handleAcrossAnimTag(childTnLst: IEGTimeNodeChoice[]) {
        const filterChildTnLst = childTnLst.filter(
            (tnBase) => !XY_ATTR_NAME_LIST.includes(this.getAttrNameFromTnBase(tnBase) ?? AttrName.none),
        );
        this.updateAttrTavLst(filterChildTnLst, WH_ATTR_NAME_LIST, PresetSubtype.Across);
        return filterChildTnLst;
    }

    private updateAttrTavLst(childTnLst: IEGTimeNodeChoice[], attrNameList: AttrName[], presetSubtype: PresetSubtype) {
        childTnLst.forEach((tnBase) => {
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (behaviorKey !== BehaviorType.anim) return;

            const attrName = this.getAttrNameFromTnBase(tnBase);
            if (!attrName || !attrNameList.includes(attrName)) return;
            const tavLst = this.getTavLstFromTnBase(tnBase);
            tavLst?.forEach((tav) => {
                const tm = tav?.tm;
                //  tm的值有可能为undefined,0,100000,
                const value = get(STRETCH_TAV_MAP, `${presetSubtype}.${attrName}.${tm}`);
                if (isUndefined(value)) return;
                const attrType = isNaN(Number(value)) ? 'strVal' : 'fltVal';
                set(tav, 'val', { [attrType]: value });
            });
        });
    }

    private handleNotAcrossAnimTag(childTnLst: IEGTimeNodeChoice[], presetSubtype: PresetSubtype) {
        if (childTnLst.length > 3) {
            // 不需要重新生成anim Tag，直接更新原来的anim中attrName对应的value值
            this.updateAttrTavLst(childTnLst, [...WH_ATTR_NAME_LIST, ...XY_ATTR_NAME_LIST], presetSubtype);
            return childTnLst;
        }
        const firstAnim = childTnLst.find((tnBase) => this.getBehaviorKeyFromTnBase(tnBase) === BehaviorType.anim);
        if (!firstAnim) return childTnLst;
        // 改变原来anim中的attrName的value，并增加两个新的anim
        // step1: 更新原来的anim中ppt_w以及ppt_h的value
        this.updateAttrTavLst(childTnLst, WH_ATTR_NAME_LIST, presetSubtype);
        // step2： 生成两个新anim
        const newAnimObjList = XY_ATTR_NAME_LIST.map((attrName) => {
            const newAnimObj = cloneDeep(firstAnim);
            this.setAttrNameInTnBase(newAnimObj, attrName);
            const tavLst = this.getTavLstFromTnBase(newAnimObj);
            tavLst?.forEach((tav) => {
                const tm = tav?.tm;
                //  tm的值有可能为undefined,0,100000,
                const value = get(STRETCH_TAV_MAP, `${presetSubtype}.${attrName}.${tm}`);
                if (isUndefined(value)) return;
                set(tav, 'val', { strVal: value });
            });
            return newAnimObj;
        });
        childTnLst.unshift(...newAnimObjList);
        return childTnLst;
    }

    /**
     * 入场-基本旋转subType处理
     * @param childTnLst
     * @param presetSubtype
     * @returns
     */
    private handleEntrSwivel(childTnLst: IEGTimeNodeChoice[], presetSubtype: PresetSubtype) {
        const tnBase = childTnLst.find((tnBase) => !!tnBase.anim);
        if (!tnBase?.anim || !(PresetSubtype.Across === presetSubtype || PresetSubtype.Vertical === presetSubtype))
            return;
        const attrName = this.getAttrNameFromTnBase(tnBase);
        if (!(attrName === AttrName.pptW || attrName === AttrName.pptH)) return;
        const subTypeMap: {
            [key: string]: {
                [key: string]: ICTTLTimeAnimateValueList;
            };
        } = {
            [PresetSubtype.Across]: {
                [AttrName.pptW]: {
                    tav: [
                        {
                            tm: '0',
                            fmla: '#ppt_w*sin(2.5*pi*$)',
                            val: { fltVal: { val: 0 } },
                        },
                        {
                            tm: '100000',
                            val: { fltVal: { val: 1 } },
                        },
                    ],
                },
                [AttrName.pptH]: {
                    tav: [
                        {
                            tm: '0',
                            val: { strVal: { val: '#ppt_h' } },
                        },
                        {
                            tm: '100000',
                            val: { strVal: { val: '#ppt_h' } },
                        },
                    ],
                },
            },
            [PresetSubtype.Vertical]: {
                [AttrName.pptW]: {
                    tav: [
                        {
                            tm: '0',
                            val: { strVal: { val: '#ppt_w' } },
                        },
                        {
                            tm: '100000',
                            val: { strVal: { val: '#ppt_w' } },
                        },
                    ],
                },

                [AttrName.pptH]: {
                    tav: [
                        {
                            tm: '0',
                            fmla: '#ppt_h*sin(2.5*pi*$)',
                            val: { fltVal: { val: 0 } },
                        },
                        {
                            tm: '100000',
                            val: { fltVal: { val: 1 } },
                        },
                    ],
                },
            },
        };
        tnBase.anim.tavLst = subTypeMap[presetSubtype][attrName];
    }

    /**
     * 退场-层叠, 不同方向对x,y,w,h四个子属性进行调整
     * @param childTnLst
     * @param presetSubtype
     */
    private handleStretch(childTnLst: IEGTimeNodeChoice[], presetSubtype: PresetSubtype) {
        const hasWH = [PresetSubtype.Across];
        const hasWHXY = [PresetSubtype.Top, PresetSubtype.Bottom, PresetSubtype.Left, PresetSubtype.Right];
        const subTypeMap = this.getStretchSubTypeConfig();
        this.adjustPresetSubType(childTnLst, presetSubtype, hasWH, [], hasWHXY, subTypeMap);
    }

    /**
     * 退出-层叠，根据ooxml的结构，不同方向，对应不同的配置项
     * @returns
     */
    private getStretchSubTypeConfig() {
        const w = AttrName.pptW;
        const h = AttrName.pptH;
        const x = AttrName.pptX;
        const y = AttrName.pptY;
        const wStr = this.getAnimVariantStrVal(w);
        const hStr = this.getAnimVariantStrVal(h);
        const xStr = this.getAnimVariantStrVal(x);
        const yStr = this.getAnimVariantStrVal(y);
        const across = {
            [w]: {
                0: wStr,
                100000: this.getAnimVariantFltVal(0),
            },
            [h]: {
                0: hStr,
                100000: hStr,
            },
        };
        const subTypeMap: {
            [key: string]: {
                [key: string]: {
                    [key: string]: ICTTLAnimVariant;
                };
            };
        } = {
            // 兼容错误的、office2006数据
            [PresetSubtype.UnSupport]: across,
            // 跨 10
            [PresetSubtype.Across]: across,
            // 到顶端 1
            [PresetSubtype.Top]: {
                [w]: {
                    0: wStr,
                    100000: wStr,
                },
                [h]: {
                    0: hStr,
                    100000: this.getAnimVariantFltVal(0),
                },
                [x]: {
                    0: xStr,
                    100000: xStr,
                },
                [y]: {
                    0: yStr,
                    100000: this.getAnimVariantStrVal(`${y}-${h}/2`),
                },
            },
            // 到底端 4
            [PresetSubtype.Bottom]: {
                [w]: {
                    0: wStr,
                    100000: wStr,
                },
                [h]: {
                    0: hStr,
                    100000: this.getAnimVariantFltVal(0),
                },
                [x]: {
                    0: xStr,
                    100000: xStr,
                },
                [y]: {
                    0: yStr,
                    100000: this.getAnimVariantStrVal(`${y}+${h}/2`),
                },
            },
            // 到左侧 8
            [PresetSubtype.Left]: {
                [w]: {
                    0: wStr,
                    100000: this.getAnimVariantFltVal(0),
                },
                [h]: {
                    0: hStr,
                    100000: hStr,
                },
                [x]: {
                    0: xStr,
                    100000: this.getAnimVariantStrVal(`${x}-${w}/2`),
                },
                [y]: {
                    0: yStr,
                    100000: yStr,
                },
            },
            // 到右侧 2
            [PresetSubtype.Right]: {
                [w]: {
                    0: wStr,
                    100000: this.getAnimVariantFltVal(0),
                },
                [h]: {
                    0: hStr,
                    100000: hStr,
                },
                [x]: {
                    0: xStr,
                    100000: this.getAnimVariantStrVal(`${x}+${w}/2`),
                },
                [y]: {
                    0: yStr,
                    100000: yStr,
                },
            },
        };
        return subTypeMap;
    }

    /**
     * 根据 presetSubType，调整对应的childTnLst
     * @param childTnLst
     * @param presetSubtype
     * @param hasWH
     * @param hasWHY
     * @param hasWHXY
     * @param subTypeMap
     */
    private adjustPresetSubType(
        childTnLst: IEGTimeNodeChoice[],
        presetSubtype: PresetSubtype,
        hasWH: PresetSubtype[],
        hasWHY: PresetSubtype[],
        hasWHXY: PresetSubtype[],
        subTypeMap: {
            [key: string]: {
                [key: string]: {
                    [key: string]: ICTTLAnimVariant;
                };
            };
        },
    ) {
        const adjustChildTnLst = this.adjustAttrVal(
            this.adjustChildTnLst(childTnLst, presetSubtype, hasWH, hasWHY, hasWHXY),
            subTypeMap,
            presetSubtype,
        );
        childTnLst.splice(0, childTnLst.length, ...adjustChildTnLst);
    }

    // 需要根据方向增减childTnlst，例如层叠-跨只需要x，y，set3个children，层叠其余方向需要x,y,w,h,set5个children
    private adjustChildTnLst(
        childTnList: IEGTimeNodeChoice[],
        presetSubtype: PresetSubtype,
        hasWH: PresetSubtype[],
        hasWHY: PresetSubtype[],
        hasWHXY: PresetSubtype[],
    ) {
        let tempChildList = [...childTnList];
        if (hasWH.includes(presetSubtype)) {
            tempChildList = childTnList.filter((tnBase) => {
                // 缩放动画的animEffect不需要判断
                if (tnBase.animEffect && this.isScale) return true;
                const attrName = this.getAttrNameFromTnBase(tnBase);
                const isWH = attrName && [AttrName.pptW, AttrName.pptH].includes(attrName);
                return isWH || tnBase.set;
            });
            return tempChildList;
        }
        if (hasWHY.includes(presetSubtype)) {
            tempChildList = childTnList.filter((tnBase) => {
                // 缩放动画的animEffect不需要判断
                if (tnBase.animEffect && this.isScale) return true;
                const attrName = this.getAttrNameFromTnBase(tnBase);
                const isWHY = attrName && [AttrName.pptW, AttrName.pptH, AttrName.pptY].includes(attrName);
                return isWHY || tnBase.set;
            });
            return tempChildList;
        }
        if (hasWHXY.includes(presetSubtype)) {
            const tnBase = childTnList.find((tnBase) => {
                const attrName = this.getAttrNameFromTnBase(tnBase);
                if (!attrName) return false;
                return attrName === AttrName.pptW;
            });
            if (!tnBase) return [];
            return [AttrName.pptX, AttrName.pptY].map((name) => {
                const cloneTnBase = cloneDeep(tnBase);
                this.setAttrNameInTnBase(cloneTnBase, name);
                return cloneTnBase;
            });
        }
        return tempChildList;
    }

    /**
     * 根据配置项，调整subType的attrName的值
     * @param childTnList
     * @param subTypeMap
     * @param presetSubType
     * @returns
     */
    private adjustAttrVal(
        childTnList: IEGTimeNodeChoice[],
        subTypeMap: {
            [key: string]: {
                [key: string]: {
                    [key: string]: ICTTLAnimVariant;
                };
            };
        },
        presetSubType: PresetSubtype,
    ) {
        return childTnList.map((tnBase) => {
            if (!tnBase.anim) return tnBase;
            const attrName = this.getAttrNameFromTnBase(tnBase);
            const cloneTnBase = cloneDeep(tnBase);
            if (!attrName) return tnBase;
            const subTypeInfo = subTypeMap[presetSubType] ?? subTypeMap[PresetSubtype.UnSupport];
            const attrInfo = subTypeInfo[attrName];
            cloneTnBase.anim?.tavLst?.tav?.forEach((tavItem) => {
                tavItem.tm && Reflect.set(tavItem, 'val', attrInfo[tavItem.tm]);
            });
            return cloneTnBase;
        });
    }

    /**
     * 处理动画XY轴
     * @param this.data
     * @param targetPresetSubType
     * @returns
     */
    private setTavLstWithSubType(targetPresetSubType: PresetSubtype) {
        if (!this.data?.par?.cTn) return;
        const { presetClass = STTLTimeNodePresetClassType.PBDEFAULT, presetId, presetSubtype } = this.data.par?.cTn;
        const childTnLst = this.data.par.cTn.childTnLst?.egTimeNodeChoice || [];
        let xIsProcessed = false;
        let yIsProcessed = false;
        childTnLst.forEach((tnBase) => {
            const { anim } = tnBase;
            const behaviorKey = this.getBehaviorKeyFromTnBase(tnBase);
            if (behaviorKey === BehaviorType.anim && anim?.tavLst?.tav?.length) {
                if (!xIsProcessed) {
                    xIsProcessed = this.processX(anim, presetClass, presetId, presetSubtype, targetPresetSubType);
                    if (xIsProcessed) return;
                }
                if (!yIsProcessed) {
                    yIsProcessed = this.processY(anim, presetClass, presetId, presetSubtype, targetPresetSubType);
                }
                return;
            }
        });
    }

    /**
     * 退场-基本旋转 presetSubtype 处理
     * @param childTnLst
     * @param presetSubtype
     * @returns
     */
    private handleExitSwivel(childTnLst: IEGTimeNodeChoice[], presetSubType: PresetSubtype) {
        if (!(presetSubType === PresetSubtype.Across || presetSubType === PresetSubtype.Vertical)) return;
        const w = AttrName.pptW;
        const h = AttrName.pptH;
        const subTypeMap = {
            [PresetSubtype.Across]: {
                [w]: {
                    name: h,
                    matchLen: 21,
                },
                [h]: {
                    name: w,
                    matchLen: 2,
                },
            },
            [PresetSubtype.Vertical]: {
                [w]: {
                    name: h,
                    matchLen: 2,
                },
                [h]: {
                    name: w,
                    matchLen: 21,
                },
            },
        };
        childTnLst.find((tnBase) => {
            if (!tnBase.anim) return false;
            const attrName = this.getAttrNameFromTnBase(tnBase);
            if (!(attrName === w || attrName === h)) return false;
            const changeConfig = subTypeMap[presetSubType][attrName];
            // 只有tnAnim中的pptw和pptH属性进行变换，其他的属性无需改变
            if (!changeConfig) return false;
            const { name, matchLen } = changeConfig;
            // presetSubtype 由水平切换至水平，或者由垂直切换至垂直，则无需改动
            const tavLst = this.getTavLstFromTnBase(tnBase);
            if (tavLst?.length === matchLen) return true;
            this.setAttrNameInTnBase(tnBase, name);
            tnBase.anim.tavLst?.tav?.forEach((tavItem) => {
                tavItem.val?.strVal?.val &&
                    Reflect.set(tavItem.val.strVal, 'val', tavItem.val?.strVal.val.replace(attrName, name));
            });
            return false;
        });
    }

    /**
     * 入场-基本缩放 presetSubtype 处理
     * @param childTnLst
     * @param presetSubtype
     */
    private handleEntrZoom(childTnLst: IEGTimeNodeChoice[], presetSubType: PresetSubtype) {
        const hasWH = [
            PresetSubtype.EleCenter,
            PresetSubtype.InSlightly,
            PresetSubtype.ToEleCenter,
            PresetSubtype.OutSlightly,
        ];
        const hasWHXY = [PresetSubtype.SlideCenter, PresetSubtype.OutBottom];
        const subTypeMap = this.getEntrZoomSubTypeConfig();
        this.adjustPresetSubType(childTnLst, presetSubType, hasWH, [], hasWHXY, subTypeMap);
    }

    /**
     * 入场-基本缩放，根据ooxml的结构，不同方向，对应不同的配置项
     */
    private getEntrZoomSubTypeConfig() {
        const w = AttrName.pptW;
        const h = AttrName.pptH;
        const x = AttrName.pptX;
        const y = AttrName.pptY;
        const [wStr, hStr, xStr, yStr] = [w, h, x, y].map((value) => this.getAnimVariantStrVal(`#${value}`));
        const [zeroFltVal, halfFltVal] = [0, 0.5].map((value) => this.getAnimVariantFltVal(value));
        const eleCenter = {
            [w]: {
                0: zeroFltVal,
                100000: wStr,
            },
            [h]: {
                0: zeroFltVal,
                100000: hStr,
            },
        };
        const subTypeMap: {
            [key: string]: {
                [key: string]: {
                    [key: string]: ICTTLAnimVariant;
                };
            };
        } = {
            // 兼容错误的、office2006数据
            [PresetSubtype.UnSupport]: eleCenter,
            // 向内 16
            [PresetSubtype.EleCenter]: eleCenter,
            // 从屏幕中心放大 528
            [PresetSubtype.SlideCenter]: {
                [w]: {
                    0: zeroFltVal,
                    100000: wStr,
                },
                [h]: {
                    0: zeroFltVal,
                    100000: hStr,
                },
                [x]: {
                    0: halfFltVal,
                    100000: xStr,
                },
                [y]: {
                    0: halfFltVal,
                    100000: yStr,
                },
            },
            // 轻微放大 272
            [PresetSubtype.InSlightly]: {
                [w]: {
                    0: this.getAnimVariantStrVal(`2/3*#${w}`),
                    100000: wStr,
                },
                [h]: {
                    0: this.getAnimVariantStrVal(`2/3*#${h}`),
                    100000: hStr,
                },
            },
            // 向外 32
            [PresetSubtype.ToEleCenter]: {
                [w]: {
                    0: this.getAnimVariantStrVal(`4*#${w}`),
                    100000: wStr,
                },
                [h]: {
                    0: this.getAnimVariantStrVal(`4*#${h}`),
                    100000: hStr,
                },
            },
            // 从屏幕底部缩小 36
            [PresetSubtype.OutBottom]: {
                [w]: {
                    // (6*min(max(#ppt_w*#ppt_h,.3),1)-7.4)/-.7*#ppt_w
                    0: this.getAnimVariantStrVal(`(6*min(max(#${w}*#${h},.3),1)-7.4)/-.7*#${w}`),
                    100000: wStr,
                },
                [h]: {
                    // (6*min(max(#ppt_w*#ppt_h,.3),1)-7.4)/-.7*#ppt_h
                    0: this.getAnimVariantStrVal(`(6*min(max(#${w}*#${h},.3),1)-7.4)/-.7*#${h}`),
                    100000: hStr,
                },
                [x]: {
                    0: halfFltVal,
                    100000: xStr,
                },
                [y]: {
                    // 1+(6*min(max(#ppt_w*#ppt_h,.3),1)-7.4)/-.7*#ppt_h/2
                    0: this.getAnimVariantStrVal(`1+(6*min(max(#${w}*#${h},.3),1)-7.4)/-.7*#${h}/2`),
                    100000: yStr,
                },
            },
            // 轻微缩小 288
            [PresetSubtype.OutSlightly]: {
                [w]: {
                    0: this.getAnimVariantStrVal(`4/3*#${w}`),
                    100000: wStr,
                },
                [h]: {
                    0: this.getAnimVariantStrVal(`4/3*#${h}`),
                    100000: hStr,
                },
            },
        };

        return subTypeMap;
    }

    /**
     * 退场-基本缩放 presetSubType 处理
     * @param presetSubType
     */
    private handleExitZoom(childTnLst: IEGTimeNodeChoice[], presetSubType: PresetSubtype) {
        const hasWH = [
            PresetSubtype.EleCenter,
            PresetSubtype.InSlightly,
            PresetSubtype.ToEleCenter,
            PresetSubtype.OutSlightly,
        ];
        const hasWHY = [PresetSubtype.ToSlideBottom];
        const hasWHXY = [PresetSubtype.ToSlideCenter];
        const subTypeMap = this.getExitZoomSubTypeConfig();
        this.adjustPresetSubType(childTnLst, presetSubType, hasWH, hasWHY, hasWHXY, subTypeMap);
    }

    /**
     *  退场-基本缩放，根据ooxml的结构，不同方向，对应不同的配置项
     * @returns
     */
    private getExitZoomSubTypeConfig() {
        const w = AttrName.pptW;
        const h = AttrName.pptH;
        const x = AttrName.pptX;
        const y = AttrName.pptY;
        const [wStr, hStr, xStr, yStr] = [w, h, x, y].map((value) => this.getAnimVariantStrVal(value));
        const [zeroFltVal, halfFltVal] = [0, 0.5].map((value) => this.getAnimVariantFltVal(value));
        const eleCenter = {
            [w]: {
                0: wStr,
                100000: this.getAnimVariantStrVal(`4*${w}`),
            },
            [h]: {
                0: hStr,
                100000: this.getAnimVariantStrVal(`4*${h}`),
            },
        };
        const subTypeMap = {
            // 兼容错误的、office2006数据
            [PresetSubtype.UnSupport]: eleCenter,
            // 向内 16
            [PresetSubtype.EleCenter]: eleCenter,
            // 缩小到屏幕中心 544
            [PresetSubtype.ToSlideCenter]: {
                [w]: {
                    0: wStr,
                    100000: zeroFltVal,
                },
                [h]: {
                    0: hStr,
                    100000: zeroFltVal,
                },
                [x]: {
                    0: xStr,
                    100000: halfFltVal,
                },
                [y]: {
                    0: yStr,
                    100000: halfFltVal,
                },
            },
            // 轻微放大 272
            [PresetSubtype.InSlightly]: {
                [w]: {
                    0: wStr,
                    100000: this.getAnimVariantStrVal(`4/3*${w}`),
                },
                [h]: {
                    0: hStr,
                    100000: this.getAnimVariantStrVal(`4/3*${h}`),
                },
            },
            // 向外 32
            [PresetSubtype.ToEleCenter]: {
                [w]: {
                    0: wStr,
                    100000: zeroFltVal,
                },
                [h]: {
                    0: hStr,
                    100000: zeroFltVal,
                },
            },
            // 放大到屏幕底部 20
            [PresetSubtype.ToSlideBottom]: {
                [w]: {
                    0: wStr,
                    // (6*min(max(ppt_w*ppt_h,.3),1)-7.4)/-.7*ppt_w
                    100000: this.getAnimVariantStrVal(`(6*min(max(${w}*${h},.3),1)-7.4)/-.7*${w}`),
                },
                [h]: {
                    0: hStr,
                    // (6*min(max(ppt_w*ppt_h,.3),1)-7.4)/-.7*ppt_h
                    100000: this.getAnimVariantStrVal(`(6*min(max(${w}*${h},.3),1)-7.4)/-.7*${h}`),
                },
                [y]: {
                    0: yStr,
                    // 1+(6*min(max(ppt_w*ppt_h,.3),1)-7.4)/-.7*ppt_h/2
                    100000: this.getAnimVariantStrVal(`1+(6*min(max(${w}*${h},.3),1)-7.4)/-.7*${h}/2`),
                },
            },
            // 轻微缩小 288
            [PresetSubtype.OutSlightly]: {
                [w]: {
                    0: wStr,
                    100000: this.getAnimVariantStrVal(`2/3*${w}`),
                },
                [h]: {
                    0: hStr,
                    100000: this.getAnimVariantStrVal(`2/3*${h}`),
                },
            },
        };

        return subTypeMap;
    }

    /**
     * 入场-缩放 entr_scale
     * @param childTnLst
     * @param presetSubtype
     */
    private handleEntrScale(childTnLst: IEGTimeNodeChoice[], presetSubType: PresetSubtype) {
        const hasWH = [PresetSubtype.EleCenter];
        const hasWHXY = [PresetSubtype.SlideCenter];
        const subTypeMap = this.getEntrScaleSubTypeConfig();
        this.adjustPresetSubType(childTnLst, presetSubType, hasWH, [], hasWHXY, subTypeMap);
    }

    /**
     * 入场-缩放，根据ooxml的结构，不同方向，对应不同的配置项
     * @returns
     */
    private getEntrScaleSubTypeConfig() {
        const w = AttrName.pptW;
        const h = AttrName.pptH;
        const x = AttrName.pptX;
        const y = AttrName.pptY;
        const [wStr, hStr, xStr, yStr] = [w, h, x, y].map((value) => this.getAnimVariantStrVal(`#${value}`));
        const [zeroFltVal, halfFltVal] = [0, 0.5].map((value) => this.getAnimVariantFltVal(value));
        const eleCenter = {
            [w]: {
                0: zeroFltVal,
                100000: wStr,
            },
            [h]: {
                0: zeroFltVal,
                100000: hStr,
            },
        };
        const subTypeMap: {
            [key: string]: {
                [key: string]: {
                    [key: string]: ICTTLAnimVariant;
                };
            };
        } = {
            // 兼容错误的、office2006数据
            [PresetSubtype.UnSupport]: eleCenter,
            // 向内 16
            [PresetSubtype.EleCenter]: eleCenter,
            // 幻灯片中心 528
            [PresetSubtype.SlideCenter]: {
                [w]: {
                    0: zeroFltVal,
                    100000: wStr,
                },
                [h]: {
                    0: zeroFltVal,
                    100000: hStr,
                },
                [x]: {
                    0: halfFltVal,
                    100000: xStr,
                },
                [y]: {
                    0: halfFltVal,
                    100000: yStr,
                },
            },
        };

        return subTypeMap;
    }

    /**
     * 退场-缩放 exit_scale
     * @param presetSubType
     */
    private handleExitScale(childTnLst: IEGTimeNodeChoice[], presetSubType: PresetSubtype) {
        const hasWH = [PresetSubtype.ToEleCenter];
        const hasWHXY = [PresetSubtype.ToSlideCenter];
        const subTypeMap = this.getExitScaleSubTypeConfig();
        this.adjustPresetSubType(childTnLst, presetSubType, hasWH, [], hasWHXY, subTypeMap);
    }

    // 退场-缩放，根据ooxml的结构，不同方向，对应不同的配置项
    private getExitScaleSubTypeConfig() {
        const w = AttrName.pptW;
        const h = AttrName.pptH;
        const x = AttrName.pptX;
        const y = AttrName.pptY;
        const [wStr, hStr, xStr, yStr] = [w, h, x, y].map((value) => this.getAnimVariantStrVal(value));
        const [zeroFltVal, halfFltVal] = [0, 0.5].map((value) => this.getAnimVariantFltVal(value));
        const eleCenter = {
            [w]: {
                0: wStr,
                100000: zeroFltVal,
            },
            [h]: {
                0: hStr,
                100000: zeroFltVal,
            },
        };
        const subTypeMap: {
            [key: string]: {
                [key: string]: {
                    [key: string]: ICTTLAnimVariant;
                };
            };
        } = {
            // 兼容错误的、office2006数据
            [PresetSubtype.UnSupport]: eleCenter,
            // 向外 32
            [PresetSubtype.ToEleCenter]: eleCenter,
            // 缩小到屏幕中心 544
            [PresetSubtype.ToSlideCenter]: {
                [w]: {
                    0: wStr,
                    100000: zeroFltVal,
                },
                [h]: {
                    0: hStr,
                    100000: zeroFltVal,
                },
                [x]: {
                    0: xStr,
                    100000: halfFltVal,
                },
                [y]: {
                    0: yStr,
                    100000: halfFltVal,
                },
            },
        };

        return subTypeMap;
    }

    private getAnimVariantVal(type: string, value: AttrName | number | string): ICTTLAnimVariant {
        return { [type]: { val: value } };
    }

    private getAnimVariantStrVal(value: AttrName | number | string) {
        return this.getAnimVariantVal('strVal', value);
    }

    private getAnimVariantFltVal(value: AttrName | number | string) {
        return this.getAnimVariantVal('fltVal', value);
    }
}

export function getPathIdMap() {
    const idTypeArr = [
        {
            idsObj: StraightLinePresetId,
            type: AnimType.StraightLine,
        },
        {
            idsObj: ArcPresetId,
            type: AnimType.Arc,
        },
        {
            idsObj: CyclePresetId,
            type: AnimType.Cycle,
        },
        {
            idsObj: ShapePresetId,
            type: AnimType.Shape,
        },
        {
            idsObj: TurnPresetId,
            type: AnimType.Turn,
        },
    ];

    const idMap: Record<number, AnimType> = {};
    idTypeArr.forEach((item) => {
        const { idsObj, type } = item;
        Object.values(idsObj).forEach((id) => {
            idMap[id] = type;
        });
    });

    return idMap;
}
